import React, { useState } from 'react';
import './BulkPrepareModal.css';

function BulkPrepareModal({
  isOpen,
  onClose,
  onStartBulkPrepare,
  availableLanguages
}) {
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);

  if (!isOpen) return null;

  // Same languages as the top dropdown (from AddLanguageModal)
  const allLanguages = [
    { code: 'de', name: 'German' },
    { code: 'ar', name: 'Arabic' },
    { code: 'hi', name: 'Hindi' },
    { code: 'pt', name: 'Portuguese' },
    { code: 'ru', name: 'Russian' },
    { code: 'it', name: 'Italian' },
    { code: 'ta', name: 'Tamil' },
    { code: 'te', name: 'Telugu' },
  ];

  // Show all languages (will add them if they don't exist)
  const newLanguages = allLanguages;

  const handleLanguageToggle = (langCode) => {
    setSelectedLanguages(prev =>
      prev.includes(langCode)
        ? prev.filter(code => code !== langCode)
        : [...prev, langCode]
    );
  };

  const handleSelectAll = () => {
    setSelectedLanguages(newLanguages.map(lang => lang.code));
  };

  const handleDeselectAll = () => {
    setSelectedLanguages([]);
  };

  const handleStartPrepare = async () => {
    if (selectedLanguages.length === 0) {
      return;
    }

    setIsProcessing(true);
    await onStartBulkPrepare(selectedLanguages);
    setIsProcessing(false);
    onClose();
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content bulk-prepare-modal">
        <h2>🚀 Bulk Prepare AI Scripts</h2>

        <div className="modal-body">
          <p className="modal-description">
            Select languages to add to the project and generate AI scripts for all timeline items.
            Languages will be created if they don't exist, then AI scripts will be generated.
            This process runs sequentially and may take several minutes per language.
          </p>

          {newLanguages.length === 0 ? (
            <div className="no-languages-available">
              <p>No languages available for bulk preparation.</p>
            </div>
          ) : (
            <>
              <div className="selection-controls">
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="select-all-button"
                  disabled={isProcessing}
                >
                  Select All
                </button>
                <button
                  type="button"
                  onClick={handleDeselectAll}
                  className="deselect-all-button"
                  disabled={isProcessing}
                >
                  Deselect All
                </button>
              </div>

              <div className="language-grid">
                {newLanguages.map((lang) => {
                  const languageExists = availableLanguages.includes(lang.code);
                  return (
                    <label key={lang.code} className={`language-checkbox ${languageExists ? 'existing' : 'new'}`}>
                      <input
                        type="checkbox"
                        checked={selectedLanguages.includes(lang.code)}
                        onChange={() => handleLanguageToggle(lang.code)}
                        disabled={isProcessing}
                      />
                      <span className="language-name">
                        {lang.name}
                        {languageExists && <span className="language-status"> ✓</span>}
                        {!languageExists && <span className="language-status"> +</span>}
                      </span>
                    </label>
                  );
                })}
              </div>

              <div className="selected-count">
                <p>{selectedLanguages.length} language(s) selected</p>
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button
            onClick={onClose}
            className="cancel-button"
            disabled={isProcessing}
          >
            Cancel
          </button>
          {newLanguages.length > 0 && (
            <button
              onClick={handleStartPrepare}
              className="start-prepare-button"
              disabled={selectedLanguages.length === 0 || isProcessing}
            >
              {isProcessing ? 'Starting...' : 'Start Bulk Prepare'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default BulkPrepareModal;